import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const PersonalInsightsSection = ({ user, onEditWhyValues, onEditAskMeAbout, onEditLanguages, onEditInterests }) => {
  const intl = useIntl();

  // Helper function to format display text: remove underscores and capitalize each word
  const formatDisplayText = (text) => {
    if (!text) return '';

    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderList = (items, emptyText) => {
    if (!items || items.length === 0) {
      return <p className="text-succession-text-muted italic">{emptyText}</p>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => (
          <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-succession-bg-primary border border-succession-border text-succession-text-primary">
            {formatDisplayText(item)}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      <h3 className="text-xl font-semibold text-succession-text-primary mb-6">{intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.title' })}</h3>

      <div className="space-y-6">
        {/* What's Your Why */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.whats_your_why' })}</h4>
            <button
              onClick={onEditWhyValues}
              className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
            </button>
          </div>
          {renderList(user.why_values, intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.no_values' }))}
        </div>

        {/* Ask Me About */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.ask_me_about' })}</h4>
            <button
              onClick={onEditAskMeAbout}
              className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
            </button>
          </div>
          {user.ask_me_about ? (
            <p className="text-succession-text-primary">{user.ask_me_about}</p>
          ) : (
            <p className="text-succession-text-muted italic">{intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.not_specified' })}</p>
          )}
        </div>

        {/* Languages */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.languages' })}</h4>
            <button
              onClick={onEditLanguages}
              className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
            </button>
          </div>
          {renderList(user.languages, intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.no_languages' }))}
        </div>

        {/* Interests & Hobbies */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.interests_hobbies' })}</h4>
            <button
              onClick={onEditInterests}
              className="inline-flex items-center px-3 py-1.5 border border-succession-border rounded-lg text-sm text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
            </button>
          </div>
          {renderList([...(user.interests || []), ...(user.sports || [])], intl.formatMessage({ id: 'succession.profile_edit.sections.personal_insights.no_interests' }))}
        </div>
      </div>
    </div>
  );
};

PersonalInsightsSection.propTypes = {
  user: PropTypes.object.isRequired,
  onEditWhyValues: PropTypes.func.isRequired,
  onEditAskMeAbout: PropTypes.func.isRequired,
  onEditLanguages: PropTypes.func.isRequired,
  onEditInterests: PropTypes.func.isRequired,
};

export default PersonalInsightsSection;
